import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, FileText, KeyRound, LayoutDashboard, Mail, Share2, Ticket, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Section, Container, Eyebrow, CtaBand } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";
import { Seo } from "@/components/shared/Seo";
import { useCredit } from "@/components/shared/MadeBy";
import { paragraphs } from "@/lib/format";

/*
 * Kolofon – who made the site and what it does. The studio credit (name, link, text) is editable under
 * Innstillingar → «Laga av»; everything else here is the house's own story about its website.
 */

const SYSTEMS = [
  { icon: CalendarDays, title: "Program og kalender", text: "Alt som skjer på huset – konsertar, øvingar og utleige – i éin kalender med ICS-eksport og utskrift av terminlista." },
  { icon: KeyRound, title: "Rombooking", text: "Førespurnad med prisestimat, kollisjonskontroll mot kalenderen og e-poststadfesting – styret godkjenner med eitt trykk." },
  { icon: Ticket, title: "Billettar og påmelding", text: "Vipps, betaling i døra eller gratis påmelding. QR-billettar, venteliste, dørsal utan nett og innslepp på mobilen." },
  { icon: Users, title: "Arrangørside", text: "Kvart lag har si eiga side: bestillingar, prisar, innslepp, omtale og plakat – med eigen innlogging for fleire personar." },
  { icon: FileText, title: "Plakat og deling", text: "Ferdige plakatar i A4, A3 og sosiale format med QR-kode, og delingskort som ser rette ut på Facebook og i Messenger." },
  { icon: LayoutDashboard, title: "Styringsverktøy for styret", text: "Bookingar, meldingar, nyheiter med KI-hjelp, nyheitsbrev, statistikk og innhald – alt på éin stad, på nynorsk." },
];

const host = (url) => { try { return new URL(url).host.replace(/^www\./, ""); } catch { return url; } };

export default function AboutSite() {
  const credit = useCredit();
  const name = credit?.name || "dei som laga sida";
  return (
    <div data-testid="about-site-page">
      <Seo title={`Om nettsida – laga av ${credit?.name || "Halsnøy Kulturhus"}`} description={`Nettsida til Halsnøy Kulturhus er designa og utvikla av ${name}${credit?.tagline ? ` – ${credit.tagline.toLowerCase()}` : ""}. Program, booking, billettar og arrangørside i eitt system.`} />
      <PageHeader eyebrow="Om nettsida" title={<>Laga på Halsnøy. <em className="t-swash">For Halsnøy.</em></>}
        intro="Nettsida er meir enn ei nettside: ho er kalenderen, bookingkontoret, billettluka og arrangørkontoret til huset – bygd frå grunnen for eit kulturhus som vert drive på dugnad." />

      {credit && (
        <Section id="kven" eyebrow="Design og utvikling" className="pt-6 sm:pt-8" tight>
          <FadeUp>
            <div className="grid gap-8 rounded-[20px] border border-border bg-card p-6 sm:p-8 lg:grid-cols-12 lg:gap-10 lg:p-10" data-testid="about-site-credit">
              <div className="lg:col-span-5">
                <p className="font-display text-[clamp(32px,4vw,56px)] font-semibold leading-[0.95] tracking-[-0.04em]" data-testid="about-site-credit-name">{credit.name}</p>
                {credit.tagline && <p className="t-lead mt-3 text-muted-foreground" data-testid="about-site-credit-tagline">{credit.tagline}</p>}
                <div className="mt-8 flex flex-wrap gap-3">
                  {credit.url && <Button asChild size="lg" data-testid="about-site-credit-link"><a href={credit.url} target="_blank" rel="noopener">{host(credit.url)} <ArrowUpRight className="h-4 w-4" /></a></Button>}
                  {credit.email && <Button asChild variant="outline" size="lg" data-testid="about-site-credit-email"><a href={`mailto:${credit.email}`}><Mail className="h-4 w-4" /> {credit.email}</a></Button>}
                </div>
              </div>
              <div className="lg:col-span-7">
                {credit.text ? paragraphs(credit.text).map((t, i) => <p key={i} className="t-body mt-4 first:mt-0 text-pretty" data-testid={`about-site-credit-text-${i}`}>{t}</p>) : null}
                <p className="t-body mt-4 text-muted-foreground text-pretty">Nettsida er eigd og drive av Halsnøy Kulturhus SA. Innhaldet – tekst, foto og program – kjem frå styret og laga som brukar huset.</p>
              </div>
            </div>
          </FadeUp>
        </Section>
      )}

      <Section id="kva" eyebrow="Kva nettsida gjer" title={<>Eitt hus. <em className="t-swash">Eitt system.</em></>} subtitle="Alt heng saman: ein booking vert eit arrangement, eit arrangement får billettar, og arrangøren får si eiga side – utan at styret må flytte data mellom verktøy.">
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-site-systems">
          {SYSTEMS.map(({ icon: Icon, title, text }, i) => (
            <FadeUp key={title} delay={i * 0.06} className="h-full">
              <li className="h-full rounded-[16px] border border-border bg-card p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-24px_rgba(28,25,23,.35)]" data-testid={`about-site-system-${i}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary"><Icon className="h-4 w-4" /></span>
                <h3 className="t-h3 mt-5">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground text-pretty">{text}</p>
              </li>
            </FadeUp>
          ))}
        </ul>
      </Section>

      <Section id="prinsipp" eyebrow="Slik er ho laga" tight>
        <Container className="!px-0">
          <dl className="grid gap-x-10 gap-y-8 border-t border-border pt-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-site-principles">
            {[
              ["Nynorsk", "Heile nettsida, e-postane og styringsverktøyet er på nynorsk – språket til øya."],
              ["Mobil først", "Innslepp i døra, dørsal og arrangørsida er laga for mobilen – òg utan dekning."],
              ["Utan sporing", "Ingen sporingskapslar eller annonsenettverk. Besøksstatistikken er anonymisert."],
              ["Open for alle", "Kontrastar, tastaturnavigasjon og skjermlesarar er tekne omsyn til frå første strek."],
            ].map(([t, d]) => (
              <div key={t}><dt className="font-display text-[20px] font-semibold tracking-[-0.02em]">{t}</dt><dd className="mt-2 text-[15px] leading-relaxed text-muted-foreground text-pretty">{d}</dd></div>
            ))}
          </dl>
        </Container>
      </Section>

      {credit && (credit.url || credit.email) && (
        <CtaBand eyebrow="Treng de noko liknande?" title={<>Lag, verksemd eller kommune – {credit.name} byggjer <em className="t-swash">det de treng.</em></>}
          text="Nettsider, bookingsystem, billettløysingar og styringsverktøy – laga på Halsnøy, for folk som har betre ting å gjere enn å slåst med teknologi.">
          {credit.email && <Button asChild variant="light" size="lg" data-testid="about-site-cta-email"><a href={`mailto:${credit.email}`}>Ta kontakt med {credit.name} <ArrowUpRight className="h-4 w-4" /></a></Button>}
          {credit.url && <Button asChild variant="lightOutline" size="lg" data-testid="about-site-cta-link"><a href={credit.url} target="_blank" rel="noopener">Sjå {host(credit.url)}</a></Button>}
        </CtaBand>
      )}

      <Container className="pb-16 sm:pb-20">
        <Eyebrow>Spørsmål om nettsida?</Eyebrow>
        <p className="t-body mt-3 max-w-2xl text-muted-foreground text-pretty">Feil, forslag eller ønske om nye funksjonar? <Link to="/kontakt" className="u-link font-medium text-foreground" data-testid="about-site-contact-link">Skriv til styret</Link> – dei tek det vidare.</p>
      </Container>
    </div>
  );
}
